// First-touch UTM capture for campaign tracking.
// MUST stay behaviorally in sync with the inline script in astro/src/layouts/BaseLayout.astro
// (same origin, same localStorage key) — see project rule on astro/React parity.

export const ATTRIBUTION_STORAGE_KEY = 'ss_attribution'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const MAX_VALUE_LENGTH = 150

export interface StoredAttribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  landing_path?: string
  captured_at?: string
}

export function captureAttribution(search: string, pathname: string): void {
  try {
    // First touch wins — never overwrite the campaign that originally brought them in
    if (localStorage.getItem(ATTRIBUTION_STORAGE_KEY)) return

    const params = new URLSearchParams(search)
    const attribution: StoredAttribution = {}
    let hasUtm = false

    for (const key of UTM_KEYS) {
      const value = params.get(key)
      if (value && value.trim()) {
        attribution[key] = value.trim().slice(0, MAX_VALUE_LENGTH)
        hasUtm = true
      }
    }

    if (!hasUtm) return

    attribution.landing_path = pathname.slice(0, MAX_VALUE_LENGTH)
    attribution.captured_at = new Date().toISOString()
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // localStorage unavailable (private browsing etc.) — attribution is best-effort
  }
}

export function getStoredAttribution(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as StoredAttribution) : null
  } catch {
    return null
  }
}

export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    // noop
  }
}
